import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/dist/query';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import {
  Candidate,
  CandidatesData,
  CandidatesPaginationQuery,
  CandidatesSearchState,
  Note,
  CandidatesApiData,
  CandidateData,
  QualityStatusTypes,
} from '../../../types/candidates';
import CandidatePositionStatusDto from '../../../types/serverTypes/CandidatePositionStatusDto';
import UpdateCandidateDto from '../../../types/serverTypes/UpdateCandidateDto';
import AddCandidateToPositionDto, {
  ApplyCandidateToPositionDto,
} from '../../../types/serverTypes/addCandidateToPositionDto';
import CandidateProfileDto from '../../../types/serverTypes/candidateProfileDto';
import CreateCandidateNoteDto from '../../../types/serverTypes/createCandidateNoteDto';
import HireCandidateDto from '../../../types/serverTypes/hireCandidateDto';
import MakeOfferToCandidateDto from '../../../types/serverTypes/makeOfferToCandidateDto';
import { UserCandidate } from '../../../types/serverTypes/userDto';
import { ReducerPaths } from '../../constants/reducerPaths';
import { ICreateUser } from '../../legacy/auth/auth.actions';

export type CandidateBuilder = EndpointBuilder<
  BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    {},
    FetchBaseQueryMeta
  >,
  | 'Candidate'
  | 'Candidates'
  | 'CandidateNotes'
  | 'CandidatePositionStatus'
  | 'Score',
  ReducerPaths.CANDIDATES
>;

export type GetCandidatesInput = {
  page: number;
  query?: CandidatesSearchState;
};

export type GetCandidatesOuput = CandidatesData;

export type LoadCandidateNotesByCPSInput = {
  candidateId: string | number;
  positionId?: string | number;
};

export type LoadCandidateNotesByCPSOutput = Note[];

export type UploadCandidateInfoInput = {
  candidateId: number;
  data: UpdateCandidateDto;
  lang?: 'en' | 'es';
};

export type UploadCandidateInfoOutput = Candidate;

export type UpdateCandidateWorkSummaryInput = {
  candidateId: number;
  data: UpdateCandidateDto;
  lang?: 'en' | 'es';
};

export type UpdateCandidateWorkSummaryOutput = Candidate;

export type UpdateCandidateScoreInput = {
  candidateId: number;
  score: number;
  lang?: 'en' | 'es';
};

export type UpdateCandidateCompletedInput = {
  candidateId: number;
  lang?: 'en' | 'es';
};

export type UpdateCandidateScoreOutput = Candidate;

export type UpdateCandidateProfileInput = {
  data: CandidateProfileDto;
  userId: number;
  candidateId: number;
};

export type UpdateCandidateProfileOutput = CandidateProfileDto;

export type SaveCandidateNoteInput = {
  data: CreateCandidateNoteDto;
  lang: 'en' | 'es';
};

export type SaveCandidateNoteOutput = Note;

export type AddCandidateToPositionInput = {
  data: AddCandidateToPositionDto;
  lang?: 'en' | 'es';
};

export type AddCandidateToPositionOutput = AddCandidateToPositionDto;

export type AddMultipleCandidatesToPositionInput = {
  candidateIds: number[];
  positionId: number;
};

export type AddMultipleCandidatesToPositionErrorOutput = {
  status: number;
  statusText: string;
  data: any[];
};

export type RemoveCandidateFromPositionInput = {
  data: AddCandidateToPositionDto;
};

export type RemoveCandidateFromPositionOuput = AddCandidateToPositionDto;

export type DeleteCandidateInput = {
  candidateId: number;
};

export type GetCandidateDeletionPermissionOutput = {
  allowed: boolean;
};

export type AddCandidateToPositionAgainInput = {
  data: AddCandidateToPositionDto;
  lang?: 'en' | 'es';
};

export type AddCandidateToPositionAgainOutput = AddCandidateToPositionDto;

export type HireCandidateInput = {
  data: HireCandidateDto;
  lang?: 'en' | 'es';
};

export type HireCandidateOutput = HireCandidateDto;

export type UpdateCandidatePositionStatusToOfferSentInput = {
  data: MakeOfferToCandidateDto;
  lang?: 'en' | 'es';
};

export type UpdateCandidatePositionStatusToOfferSentOutput =
  MakeOfferToCandidateDto;

export type FindHiredCandidatePositionStatusByCompanyInput = {
  lang?: 'en' | 'es';
};

export type StatusCandidatePositionOptions =
  | 'requestInterview'
  | 'hired'
  | 'offerSent'
  | 'declined'
  | 'new'
  | 'terminated'
  | 'interested'
  | 'shortlisted';

export type UpdateCandidatePositionStatusData = {
  CandidatePositionStatusId: number;
  Status: StatusCandidatePositionOptions;
};

export type UpdateCandidatePositionStatusInput = any;

export type UpdateCandidatePositionStatusOutput = {
  data: UpdateCandidatePositionStatusData;
  lang?: 'en' | 'es';
};

export type UpdateCandidatePositionHighlightInput = {
  candidatePositionStatusId: number;
  highlightCandidate: boolean;
};

export type UpdateCandidatePositionHighlightOutput =
  UpdateCandidatePositionHighlightInput;

export type FindHiredCandidatePositionStatusByCompanyOutput =
  CandidatePositionStatusDto[];

export type GetInternalCandidatesInput = {
  query?: CandidatesPaginationQuery;
  lang?: 'en' | 'es';
};

export type GetInternalCandidatesOutput = CandidatesData;

export type EmailInternalAuthInput = {
  user: ICreateUser;
  lang?: 'en' | 'es';
  siteVersion: string;
};

export type GetScoreInput = { id: number };

export type GetScoreOuput = Candidate;

export type EmailInternalAuthOutput = UserCandidate;

export type GetCandidatesByPositionIdInput = {
  positionId: number;
};

export type GetCandidatesByPositionIdOuput = CandidatesApiData | undefined;

// all this values are retrieved from querystring, thats why everyone is string type
export type FindCandidatesInput = {
  SkillsMatch?: string;
  AdditionalTagsMatch?: string;
  AdditionalTags?: string;
  EducationLevel?: string;
  Files?: string;
  Languages?: string;
  Skills?: string;
  EnglishLevel?: string;
  Id?: string;
  PreviousExperience?: string;
  MinSalary?: string;
  MaxSalary?: string;
  Query?: string;
  Name?: string;
  Email?: string;
  YearsOfExperience?: string;
  page?: number;
};

export type FindCandidatesOutput = {
  data: CandidateData[];
  total: number;
  offset: number;
  limit: number;
  prev: {
    offset: number;
    limit: number;
  } | null;
  next: {
    offset: number;
    limit: 25;
  } | null;
};

export type ApplyCandidateInput = {
  data: ApplyCandidateToPositionDto;
  lang?: 'en' | 'es';
};

export type ApplyCandidateOutput = ApplyCandidateToPositionDto;

export type SearchCandidateInput = {
  Keyword: string;
  Type: string;
  Files?: string;
  QualityStatus?: string;
  OpenToWork?: string;
  Salary?: string;
  Country?: string[];
  CountryGroup?: string[];
  Categories?: string;
  orderBy?: string;
  Order?: 'ASC' | 'DESC';
  TenantIds?: string[];
  page: number;
  take: number;
  UserQuery: Record<SearchCandidateFilterTypes, string[]>;
};

export enum SearchCandidateFilterTypes {
  AllFields = 'All Fields',
  Skills = 'Skills',
  JobTitle = 'Job Title',
}

export type SearchCandidateOutput = {
  total: number;
  offset: number;
  limit: number;
  prev: null | {
    offset: number;
    limit: number;
  };
  next: null | {
    offset: number;
    limit: number;
  };
  data: {
    Id: number;
    Score: number;
    WorkSummary: null | string;
    LastLogin: string;
    ResidenceCountry: string | null;
    Country: { Name: string; Id: number; CountryGroup: string } | {};
    Files?: {
      Url: string;
      Description: string;
      FileCategory: {
        CategoryName: string;
      };
    }[];
    QualityStatus: string | null;
    OpenToWork: string | null;
    OpenToWorkUpdatedOn: string | null;
    User: {
      FirstName: string;
      LastName: string;
      Email: string;
      ProfilePicture: null | string;
    };
    CandidateJobInterest: {
      SalaryLowerBound: null | number;
      SalaryUpperBound: null | number;
    };
    CandidateSkills: {
      Skill: {
        Title: string;
      };
    }[];
    CandidateWorkExperience: {
      JobTitle: string;
      Company: string;
      Admission: string;
      Egress: null | string;
      JobPosition: {
        title: string;
      };
    }[];
    CandidateEducation: [
      {
        DegreeType: null | string;
        DegreeName: string;
        EducationalInstitutionName: string;
        FinishDate: string;
        CurrentlyEnrolled: boolean;
      },
    ];
    CreateTime: string;
    Curriculum: string;
    AdditionalVideos: {
      Id: number;
      Name: string;
      Key: string;
      Url: string;
    }[];
  }[];
};

export type UpdateCandidateQualityStatusInput = {
  QualityStatus: QualityStatusTypes;
  CandidateId: number;
};

export type UpdateCandidateQualityStatusOutput = {
  Id: number;
  QualityStatus: string;
};

export type GetShareCandidatesOutput = {
  encodedId: string;
};

export type GetShareCandidatesInput = {
  name: string;
  ids: number[];
  fee?: number;
};

export type GetSharedCandidatesRosterDataInput = {
  sharedRosterId: string;
};

export type SendShortlistRequestInterviewEmailInput = {
  CandidateId: number;
  ShortlistTitle: string;
};

export type GetSharedCandidatesRosterDataOutput = {
  positionName: string;
  fee?: number;
  candidates: {
    Id: number;
    AIData: {
      english_language_proficiency: {
        cefr_score: string;
        clarity: string;
        accent: string;
        simera_score: number;
        fluency: string;
        vocabulary_range: string;
      };
      voice: {
        tones: string[];
        volume: string;
        pace: string;
      };
      facial_expression: {
        expressions: string[];
        consistency: string;
        relevance_to_content: string;
      };
      emotion_detection: {
        emotions: string[];
        intensity: {
          happy: string;
          focused: string;
          confident: string;
          anxious: string;
          curious: string;
          disappointed: string;
          excited: string;
          relieved: string;
        };
      };
      posture_and_gesture: {
        body_movements: string;
        postures: string;
        eye_contact: string;
        gestures: string;
      };
      behavioral_patterns: {
        patterns: string[];
        consistency: string;
        situational_appropriateness: string;
      };
      personality_traits: {
        traits: string[];
        dominant_trait: string;
      };
      jump_around: {
        average_years_per_job: number;
        total_jobs: number;
        score: number;
        recent_trend: string;
      };
      job_fits: {
        name: string;
        reason: string;
        match_score: number;
        industry_experience: string;
        salary_min: number;
        salary_max: number;
      }[];
      experience_levels: {
        job_name: string;
        years_of_experience: number;
        skill_relevance: string;
      }[];
      professionalism: {
        curriculum_presentation: number;
        video_presentation: number;
        overall: number;
        attire: string;
      };
      soft_skills: {
        communication: number;
        leadership: number;
        adaptability: number;
        critical_thinking: number;
        teamwork: number;
        problem_solving: number;
        organization: number;
        creativity: number;
        persuasion: number;
        open_to_criticism: number;
        time_management: number;
        empathy: number;
        conflict_resolution: number;
      };
      sentiment: {
        positive: number;
        neutral: number;
        negative: number;
        overall_sentiment: string;
      };
      keywords: {
        word: string;
        count: number;
        context: string;
      }[];
    } | null;
    User: {
      Id: number;
      FirstName: string;
      LastName: string;
      ProfilePicture: {
        Id: number;
        Url: string;
        Key: string;
      };
      LastLogin: string;
      CreateTime: string;
    };
    Curriculum: {
      Id: number;
      Url: string;
      Key: string;
    } | null;
    AdditionalVideos: {
      Id: number;
      Url: string;
      Key: string;
      Name: string;
    }[];
    CandidateSkills: {
      IsVerified?: boolean;
      Id: number;
      Skill: {
        title: string;
      };
    }[];
    CandidateWorkExperience: {
      Id: number;
      JobTitle: string;
      Company: string;
      Admission: string;
      Egress: string;
      Description: string;
    }[];
    Country: string | null;
    ResidenceCountry: string | null;
    UpdateTime: string;
    WorkSummary: string | null;
    CandidateEducation: {
      DegreeName: string;
      EducationalInstitutionName: string;
    }[];
    CandidateJobInterest: {
      Id: number;
      SalaryLowerBound: number;
      SalaryUpperBound: number;
    } | null;
    CustomUrl: string | null;
    GithubUrl: string | null;
    LinkedinUrl: string | null;
    BehanceUrl: string | null;
    CandidateScores: {
      Id: number;
      SystemValue: null | number | string;
      SystemScore: null | number;
      OverrideValue: null | number | string;
      OverrideScore: null | number;
      CandidateScoreType: {
        Id: number;
        Name: string;
        Slug: string;
        ScoreGroup: string;
      };
    }[];
    Highlight: boolean;
    YearsOfExperience: number;
  }[];
};
